import {MessageSquare, Phone} from 'lucide-react';
import {useIntl} from 'react-intl';
import {Call} from '../../store/features/calls/callsService';
import {formatCallHistoryTime} from '../../utilis/timeUtilis';
import {ClickToCall} from '../../components/ClickToCall/ClickToCall';
import {useOpenDirectChat} from '../../hooks/useOpenDirectChat';
import {useIsChatEnabledQuery} from '../../services/chatService';
import {callRowView} from './callRowView';
import {CallerAvatar, DirectionBadge, InternalBadge, LineTile, lucide, NumberFlag, SpamBadge} from './callParts';
import {isGroupCall} from '../../utilis/callsUtilis';
import GroupCallRow from './GroupCallRow';

export interface CallRowProps {
    call: Call;
    isMobile: boolean;
    handleCallClick: (call: Call) => void;
    selectedCall: Call | undefined;
}

const hoverAction = 'inline-flex size-7 cursor-pointer items-center justify-center rounded-chip bg-surface text-ink-secondary hover:text-ink';

const CallRow = (props: CallRowProps) =>
    isGroupCall(props.call)
        ? <GroupCallRow call={props.call} isMobile={props.isMobile} isSelected={props.selectedCall?.callId === props.call.callId} onSelect={props.handleCallClick}/>
        : <SingleCallRow {...props}/>;

// Design System ListRow for a call: 48px, avatar 32, name callout 600, one-line
// subline, trailing time and line tile that give way to Call / Message on hover.
const SingleCallRow = ({call, isMobile, handleCallClick, selectedCall}: CallRowProps) => {
    const intl = useIntl();
    const view = callRowView(call);
    const isSelected = selectedCall?.callId === call.callId;
    const openDirectChat = useOpenDirectChat();
    const {data: isChatEnabled} = useIsChatEnabledQuery();
    const time = formatCallHistoryTime(call.startedAt, new Date(), intl.formatMessage({id: 'time.yesterday'}));
    const callTarget = call.internalCallee ? call.internalCallee.userId : call.clientMsisdn;

    return (
        <div
            role="button"
            tabIndex={0}
            aria-current={isSelected ? 'true' : undefined}
            onClick={() => handleCallClick(call)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCallClick(call);
                }
            }}
            data-testid="call-row"
            className={`group flex cursor-pointer items-center gap-3 rounded-control px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                isMobile ? 'h-14' : 'h-12'
            } ${isSelected ? 'bg-brand-tint-subtle' : 'hover:bg-surface-muted'}`}
        >
            <CallerAvatar call={call} view={view}/>
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 items-center gap-2 text-callout font-semibold text-ink">
                    {!view.known && call.clientMsisdn && <NumberFlag msisdn={call.clientMsisdn}/>}
                    <span className="truncate">{view.name}</span>
                    {view.spam && <SpamBadge/>}
                    {view.internal && <InternalBadge/>}
                </div>
                <div className="mt-0.5 flex min-w-0 items-center gap-2">
                    <DirectionBadge view={view}/>
                    {view.number && <span className="truncate text-footnote text-ink-muted">{view.number}</span>}
                </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 group-hover:hidden group-focus-within:hidden">
                <span className={`text-caption ${view.missed ? 'text-danger-ink' : 'text-ink-muted'}`}>{time}</span>
                <LineTile call={call}/>
            </div>

            {callTarget ? (
                <div className="hidden shrink-0 items-center gap-1 group-hover:flex group-focus-within:flex" onClick={(e) => e.stopPropagation()}>
                    <ClickToCall
                        renderAs="button"
                        msisdn={Number(callTarget)}
                        contact={call.internalCallee ?? call.primaryContact}
                        internal={Boolean(call.internalCallee)}
                        className={hoverAction}
                    >
                        <span className="sr-only">Call {view.name}</span>
                        <Phone size={16} aria-hidden="true" className={lucide}/>
                    </ClickToCall>
                    {call.internalCallee && isChatEnabled !== false && (
                        <button
                            type="button"
                            aria-label={`Message ${view.name}`}
                            onClick={() => openDirectChat(call.internalCallee.userId)}
                            className={hoverAction}
                        >
                            <MessageSquare size={16} aria-hidden="true" className={lucide}/>
                        </button>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default CallRow;
